import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { Select } from "../../../../ui/components/Select";
import { StatusBadge } from "../components/StatusBadge";
import { FeatherCalendar, FeatherArrowLeft } from "@subframe/core";
import { listChecklists } from "../../data/api/monthlyChecklistApi";
import { MonthlyChecklistDTO } from "../../data/models/MonthlyChecklistDTOs";
import { useToast } from "../../../../shared/presentation/components";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";

export const MonthlyChecklistsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [checklists, setChecklists] = useState<MonthlyChecklistDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<string>("all");

  // Generate list of months (last 12 months)
  const generateMonths = (): { value: string; label: string }[] => {
    const months: { value: string; label: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthYear = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}`;
      const monthName = date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      months.push({ value: monthYear, label: monthName });
    }
    return months;
  };

  const months = generateMonths();
  const currentMonth = `${new Date().getFullYear()}-${String(
    new Date().getMonth() + 1,
  ).padStart(2, "0")}`;

  useEffect(() => {
    const fetchChecklists = async () => {
      try {
        setLoading(true);
        const response = await listChecklists();
        if (response.success && response.data) {
          setChecklists(response.data);
          // Set default to current month if available (only if still on "all")
          if (response.data.length > 0 && selectedMonth === "all") {
            const currentChecklist = response.data.find(
              (c) => c.month_year === currentMonth,
            );
            if (currentChecklist) {
              setSelectedMonth(currentMonth);
            } else if (response.data.length > 0) {
              setSelectedMonth(response.data[0].month_year);
            }
          }
        }
      } catch (error: any) {
        showError(error.message || "Failed to load checklists");
      } finally {
        setLoading(false);
      }
    };

    fetchChecklists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatMonthYear = (monthYear: string): string => {
    const [year, month] = monthYear.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const getStatusBadge = (status: string) => {
    return <StatusBadge status={status as any} />;
  };

  const filteredChecklists =
    selectedMonth === "all"
      ? checklists
      : checklists.filter((c) => c.month_year === selectedMonth);

  return (
    <DefaultPageLayout>
      <div className="flex w-full h-full flex-col items-start">
        {/* Header */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background p-6">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="neutral-secondary"
                icon={<FeatherArrowLeft />}
                onClick={() => navigate("/admin?tab=checklist")}
              >
                Back to Template
              </Button>
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Month End Checklists
                </span>
                <span className="text-body font-body text-subtext-color">
                  View and manage monthly checklists
                </span>
              </div>
            </div>
          </div>

          {/* Month Selector */}
          <div className="flex items-center gap-2">
            <span className="text-body font-body text-default-font">
              Filter by Month:
            </span>
            <Select
              value={selectedMonth}
              onValueChange={(value: string) =>
                setSelectedMonth(value || "all")
              }
              placeholder="Select Month"
              className="w-48"
            >
              <Select.Item value="all">All Months</Select.Item>
              {months.map((month) => (
                <Select.Item key={month.value} value={month.value}>
                  {month.label}
                </Select.Item>
              ))}
            </Select>
          </div>
        </div>

        {/* Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 overflow-y-auto bg-default-background p-8">
          {loading ? (
            <div className="flex w-full items-center justify-center p-8">
              <span className="text-body font-body text-subtext-color">
                Loading checklists...
              </span>
            </div>
          ) : filteredChecklists.length === 0 ? (
            <div className="flex w-full flex-col items-center justify-center gap-4 p-12 bg-neutral-50 border border-neutral-border rounded-lg">
              <FeatherCalendar className="w-12 h-12 text-subtext-color" />
              <div className="flex flex-col items-center gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  No Checklists Found
                </span>
                <span className="text-body font-body text-subtext-color text-center max-w-md">
                  {selectedMonth !== "all"
                    ? `No checklist found for ${formatMonthYear(selectedMonth)}`
                    : "No checklists have been created yet"}
                </span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
              {filteredChecklists.map((checklist) => (
                <div
                  key={checklist.id}
                  className="flex flex-col gap-4 p-6 border border-solid border-neutral-border rounded-lg bg-default-background hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() =>
                    navigate(`/admin/month-end-checklists/${checklist.id}`, {
                      state: { month_year: checklist.month_year },
                    })
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <span className="text-heading-3 font-heading-3 text-default-font">
                        {formatMonthYear(checklist.month_year)}
                      </span>
                      {checklist.template_name && (
                        <span className="text-body font-body text-subtext-color text-sm">
                          {checklist.template_name}
                        </span>
                      )}
                    </div>
                    {getStatusBadge(checklist.status)}
                  </div>

                  {checklist.progress &&
                    (() => {
                      const p = checklist.progress as {
                        total?: number;
                        completed?: number;
                        done?: number;
                      };
                      const completed = p.completed ?? p.done ?? 0;
                      const total = p.total ?? 0;
                      return (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-body font-body text-subtext-color">
                              Progress
                            </span>
                            <span className="text-body font-body text-default-font font-semibold">
                              {completed} / {total} tasks
                            </span>
                          </div>
                          <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 transition-all"
                              style={{
                                width: `${total > 0 ? (completed / total) * 100 : 0}%`,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })()}

                  {checklist.status === "closed" && checklist.closed_at && (
                    <div className="text-xs text-body font-body text-subtext-color">
                      Closed on:{" "}
                      {new Date(checklist.closed_at).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        },
                      )}
                    </div>
                  )}

                  <Button
                    variant="neutral-secondary"
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/month-end-checklists/${checklist.id}`, {
                        state: { month_year: checklist.month_year },
                      });
                    }}
                  >
                    View Details
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DefaultPageLayout>
  );
};
